import { Api, FullRequestParams, Token, HttpResponse } from './api/Api';

const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';

let isRefreshing = false;
let refreshSubscribers: ((token: string | null) => void)[] = [];

// In-memory cache store
const cache = new Map<string, HttpResponse<any, any>>();

const resourceMapping: Record<string, string[]> = {
  projects: ['/api/v1/projects'],
  sites: ['/api/v1/sites', '/api/v1/projects/'],
  'carbon-analysis': ['/api/v1/projects/{project_id}/carbon', '/api/v1/sites/{site_id}/carbon'],
  'biodiversity-analysis': ['/api/v1/projects/{project_id}/biodiversity', '/api/v1/sites/{site_id}/biodiversity'],
  'environmental-analysis': ['/api/v1/projects/{project_id}/environmental', '/api/v1/sites/{site_id}/environmental'],
  admin: ['/api/v1/admin'],
  inference: ['/api/v1/admin/inference'],
  assignments: ['/api/v1/assignments'],
  users: ['/api/v1/auth/user', '/api/v1/admin/users'],
  'soil-chemistry': ['/api/v1/soil-chemistry'],
};

const getResourceFromPath = (path: string): string | null => {
  for (const resource in resourceMapping) {
    if (resourceMapping[resource].some(p => path.startsWith(p))) {
      return resource;
    }
  }
  return null;
};


// Helper to generate a cache key for GET requests
const getCacheKey = (params: FullRequestParams): string | null => {
  if (params.method === 'GET') {
    const resource = getResourceFromPath(params.path);
    const resourceKey = resource ? `resource:${resource}::` : '';
    return `${resourceKey}${params.path}${params.query ? JSON.stringify(params.query) : ''}`;
  }
  return null;
};

const saveTokensToStorage = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const getAccessTokenFromStorage = (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY);
const getRefreshTokenFromStorage = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);

const clearTokensFromStorage = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  cache.clear(); // Clear cache on logout
  isRefreshing = false; // Reset the refreshing state
  refreshSubscribers = []; // Clear subscribers
  // Here you might want to redirect to login or notify other parts of the app
  console.log('Tokens cleared, user logged out.');
};

// Create an instance of the Api class with the base URL for your API
// You can use environment variables to set the base URL
const clientService = new Api({ // Using 'any' for SecurityDataType as it's not strictly typed here
  baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:8000', // Fallback URL
  securityWorker: async () => {
    const token = getAccessTokenFromStorage();
    if (token) {
      return {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
    }
    return {}; // Return empty object if no token
  },
});

// Store the original request method from the HttpClient instance
const originalRequest = clientService.request.bind(clientService);

const onRefreshed = (token: string | null) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

// Paths to exclude from caching. (These paths will not be cached)
const CACHE_EXCLUDED_PATHS = [
  '/api/v1/auth/login',
  '/api/v1/auth/signup',
  '/api/v1/auth/refresh',
  '/api/v1/sites', // This exceptional usecase to fetch analysis status of the sites
  '/api/v1/admin/files', // Exclude files endpoint from cache to get real-time status updates
  '/api/v1/soil-chemistry', // Exclude soil chemistry to always get fresh data
];

clientService.request = async <T = any, E = any>(params: FullRequestParams): Promise<HttpResponse<T, E>> => {
  const isAuthPath = params.path.includes('/api/v1/auth/login') || params.path.includes('/api/v1/auth/signup');
  const isRefreshPath = params.path.includes('/api/v1/auth/refresh');
  const isExcludedFromCache = CACHE_EXCLUDED_PATHS.some(p => params.path.includes(p));

  // Attempt to retrieve from cache for GET requests
  const cacheKey = getCacheKey(params);
  if (cacheKey && !isExcludedFromCache && cache.has(cacheKey)) {
    console.log(`[clientService] Cache hit for: ${cacheKey}`);
    return Promise.resolve(cache.get(cacheKey)! as HttpResponse<T, E>);
  }

  // Ensure 'secure: true' is set for non-auth/non-refresh requests to trigger securityWorker,
  // unless explicitly set otherwise.
  if (!isAuthPath && !isRefreshPath && params.secure === undefined) {
    params.secure = true;
  }
  try {
    // @ts-ignore due to dynamic T, E types with specific HttpResponse
    const response = await originalRequest<T, E>(params);

    // Cache successful GET responses
    if (cacheKey && !isExcludedFromCache && response.ok) {
      console.log(`[clientService] Caching response for: ${cacheKey}`);
      cache.set(cacheKey, response);
    }
    if ((params.method === 'POST' || params.method === 'PUT' || params.method === 'DELETE' || params.method === 'PATCH') && response.ok) {
      const resource = getResourceFromPath(params.path);
      if (resource) {
        console.log(`[clientService] Invalidating cache for resource: ${resource}`);
        for (const key of cache.keys()) {
          if (key.startsWith(`resource:${resource}::`)) {
            cache.delete(key);
          }
        }
      } else {
        console.log(`[clientService] No resource mapping found for path: ${params.path}. Clearing entire cache.`);
        cache.clear();
      }
    }
    return response;
  } catch (error: any) {
    const originalRequestParams = { ...params }; // Clone params for retry

    if (error && error.status === 401 && !isRefreshPath) {
      if (!isRefreshing) {
        isRefreshing = true;
        const refreshToken = getRefreshTokenFromStorage();
        console.log('[clientService] Attempting token refresh. Refresh token from storage:', refreshToken); // Diagnostic log

        if (!refreshToken) {
          clearTokensFromStorage();
          console.error("No refresh token available. Logging out.");
          isRefreshing = false; // Reset flag
          throw error;
        }

        try {
          // Use originalRequest directly for refresh to avoid recursive 401 handling
          // and to ensure it doesn't try to use the expired access token via securityWorker.
          const refreshResponse = await originalRequest<Token, any>({
            path: `/api/v1/auth/refresh`,
            method: "POST",
            format: "json",
            headers: {
              "Authorization": "Bearer " + refreshToken, // Use the refresh token for the refresh request
            },
            secure: false, // Explicitly false, refresh token handled by backend (e.g. HttpOnly cookie or specific mechanism)
          });

          saveTokensToStorage(refreshResponse.data.access_token, refreshResponse.data.refresh_token);
          isRefreshing = false;
          onRefreshed(refreshResponse.data.access_token);
          // Retry original request
          // @ts-ignore
          const retriedResponse = await originalRequest<T, E>(originalRequestParams);
          // Cache successful GET responses after retry
          if (cacheKey && !isExcludedFromCache && retriedResponse.ok) { // Use original cacheKey
            console.log(`[clientService] Caching response after refresh for: ${cacheKey}`);
            cache.set(cacheKey, retriedResponse);
          }
          return retriedResponse;
        } catch (refreshError: any) {
          isRefreshing = false;
          clearTokensFromStorage();
          console.error('Token refresh failed:', refreshError.message || refreshError);
          onRefreshed(null); // Notify subscribers that refresh failed
          throw refreshError;
        }
      } else {
        return new Promise<HttpResponse<T, E>>((resolve, reject) => {
          refreshSubscribers.push((newToken: string | null) => {
            if (newToken) {
              originalRequest<T, E>(originalRequestParams).then(responseAfterRefresh => {
                // Cache successful GET responses after queued refresh
                if (cacheKey && !isExcludedFromCache && responseAfterRefresh.ok) { // Use original cacheKey
                  console.log(`[clientService] Caching response after queued refresh for: ${cacheKey}`);
                  cache.set(cacheKey, responseAfterRefresh);
                }
                resolve(responseAfterRefresh);
              }).catch(reject);
            } else {
              reject(error); // Refresh failed, reject original error
            }
          });
        });
      }
    }
    throw error; // Re-throw other errors or if not 401
  }
};

export const saveAuthTokens = (tokenResponse: Token) => {
  saveTokensToStorage(tokenResponse.access_token, tokenResponse.refresh_token);
};

export const clearAuthTokens = () => {
  clearTokensFromStorage();
};

export const isAuthenticated = (): boolean => {
  return !!getAccessTokenFromStorage();
};

export default clientService;
